import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Sparkles, Users, CalendarClock, TrendingUp, GraduationCap, Workflow, LayoutDashboard, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: Users, t: "AI-Assisted Matching", d: "Rank caregivers by skills, availability and proximity." },
  { icon: CalendarClock, t: "Scheduling Recommendations", d: "Smart suggestions to fill shifts and reduce gaps." },
  { icon: TrendingUp, t: "Workforce Forecasting", d: "Anticipate demand and staffing needs." },
  { icon: GraduationCap, t: "Training Recommendations", d: "Suggest the right courses for each team member." },
  { icon: Workflow, t: "Automated Workflows", d: "Cut administrative busywork with automation." },
  { icon: LayoutDashboard, t: "BI Dashboards", d: "Operational analytics and business intelligence." },
];
const METRICS = [["Active Caregivers", "248"], ["Available Now", "63"], ["Open Shifts", "17"], ["Training Completion", "92%"], ["Care Requests", "31"], ["Coverage", "9 counties"]];

export default function Technology() {
  const navigate = useNavigate();
  return (
    <div>
      <section className="bg-blue-950 text-white overflow-hidden relative">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3 flex items-center gap-2"><Brain className="w-4 h-4" /> T5 Intelligence</p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold max-w-3xl leading-[1.05]">AI built around <span className="text-teal-400">human care.</span></h1>
          <p className="mt-5 text-blue-100/85 max-w-2xl text-lg">T5 Intelligence helps organizations reduce administrative work and make better operational decisions — while professional judgment always stays with your people.</p>
          <div className="mt-7"><Button onClick={() => navigate("/request-demo")} className="h-12 px-7 bg-teal-500 hover:bg-teal-600 text-white">Request a Demo</Button></div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <div className="bg-white rounded-2xl p-5 shadow-2xl border border-blue-900/5">
          <div className="flex items-center justify-between mb-4"><p className="font-display font-bold text-blue-950">T5 Intelligence · Operations</p><span className="text-xs bg-teal-100 text-teal-700 px-2.5 py-1 rounded-full font-semibold">Live demo</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {METRICS.map(([l, v]) => <div key={l} className="rounded-xl bg-slate-50 border border-slate-100 p-3"><p className="font-display text-lg font-bold text-blue-950">{v}</p><p className="text-[11px] text-slate-500 leading-tight">{l}</p></div>)}
          </div>
          <div className="mt-4 rounded-xl bg-blue-950 text-white p-3 flex items-start gap-2.5"><Sparkles className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" /><p className="text-xs leading-relaxed"><span className="font-semibold text-teal-300">T5 Assistant:</span> "Find qualified caregivers available near this client." → 3 matches ranked by skills, availability & proximity.</p></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
              className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 mb-4"><f.icon className="w-6 h-6" /></span>
              <h3 className="font-display font-semibold text-blue-950">{f.t}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl bg-teal-50 border border-teal-100 p-5 flex items-start gap-3 max-w-3xl mx-auto">
          <ShieldCheck className="w-5 h-5 text-teal-700 mt-0.5 shrink-0" />
          <p className="text-sm text-teal-900">AI assists with recommendations and workflows. It does not independently make medical diagnoses, treatment decisions, employment decisions or eligibility determinations.</p>
        </div>
      </section>
    </div>
  );
}
